import type { SyntheticEvent } from "react";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { UscDispatchType } from "../redux/store";
import type { QueryType } from "../../../common-deprecated/server/types";
import { objectToQueryString } from "../../../common-deprecated/utils";
import { setStep } from "../redux/actions/UscNavActions";
import { HistoryType, pushQueryParams } from "../../../common-deprecated/History";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import type { UscCheckoutType, UscContext, VehicleType } from "../../shared-logic/types/UscCommonTypes";
import { UscNavStepEnum } from "./constants";
import type { UscTrackingLocation } from "./tracking";
import type { BasketConfigurationType } from "./basketUtils";

// ----------------------------------------------------------------------
// 'Internal URL' utils
// ----------------------------------------------------------------------

export type UscNavigationParamsType = {
    dispatch: UscDispatchType;
    currentStep: UscNavStepEnum;
    vehicle: VehicleType | null;
    context: UscContext;
    activeFinanceOption: FinanceOptionType;
    userTaxLocationId: string | null;
    uscSettings: UscSettingsType;
    commonSettings: CommonSettingsType;
    checkoutType: UscCheckoutType | null;
    // Initially, the following props weren't part of navigation params as it wasnt strictly related to navigation logic but rather CTA logic.
    // In practice, it needed to be propagated every time navigation params were propagated so it made more sense to add it anyway.
    dealer: DealerResultType | null;
    checkoutCallback: (checkoutType?: UscCheckoutType | null) => void;
    carFilterUrl: string;
    trackingLocation: UscTrackingLocation;
    // Only used for local contact dealer form.
    tyCode?: string;
    calculationId?: string;
    financeQuoteId: string | null;
    localContactDealerClick: () => void;
    configuration?: BasketConfigurationType;
};

export type UscLinkPropsType = { onClick?: (event: React.SyntheticEvent) => void; href?: string };

/**
 * Retrieve the 'next' or 'previous' step based on the current nav params.
 *
 * Can be used on its own if there is logic which needs to know what the actual next step is.
 * This logic is also supported in getUscInternalLink.
 */
export const getUscStepSibling = (params: UscNavigationParamsType, target: "next" | "prev"): UscNavStepEnum => {
    const { currentStep } = params;

    const steps = [UscNavStepEnum.Pdp, UscNavStepEnum.Extras];
    const currentIndex = steps.indexOf(currentStep);
    const nextIndex = currentIndex + (target === "prev" ? -1 : 1);

    let nextStep: UscNavStepEnum;
    if (steps[nextIndex]) {
        nextStep = steps[nextIndex];
    } else {
        // Fallback, this is technically 'wrong' but in most cases the URL will either not be used or hidden.
        nextStep = currentStep;
    }

    return nextStep;
};

/**
 * Retrieve href/onClick properties for an internal USC link.
 *
 * Will use SPA behaviour when possible, includes logic from getUscStepSibling for ease of use.
 */
export const getUscInternalLink = (
    params: UscNavigationParamsType,
    target: UscNavStepEnum | "next" | "prev",
    checkoutType?: UscCheckoutType,
): UscLinkPropsType => {
    const { dispatch, currentStep, activeFinanceOption, commonSettings, userTaxLocationId } = params;

    const step = target === "next" || target === "prev" ? getUscStepSibling(params, target) : target;

    const newParams: QueryType = {
        ...commonSettings.query,
        uscStep: step,
        financeOption: activeFinanceOption,
    };

    /**
     * Only used in the checkout flow for CTAs.
     * If the checkout type is not set, remove it from the URL (user traveled back to PDP)
     */
    if (checkoutType) newParams.checkoutType = checkoutType;
    else delete newParams.checkoutType;

    if (userTaxLocationId) newParams.taxLocationId = userTaxLocationId;

    return {
        href: `?${objectToQueryString(newParams)}`,
        onClick: (event: SyntheticEvent): void => {
            event.preventDefault();

            if (currentStep === target) return;

            dispatch(setStep(step));
            // Should state also be updated here?
            pushQueryParams(newParams, HistoryType.Usc);
        },
    };
};
